import { useEffect, useState } from 'react';
import { Card, Button, Spinner, FormField, Badge } from '../components/ui';

const TRADES = [
  { value:'',               label:'All trades',      icon:'🔧' },
  { value:'gas_engineer',   label:'Gas Engineer',    icon:'🔥', desc:'Gas Safety Certificates, boiler servicing, installations' },
  { value:'electrician',    label:'Electrician',     icon:'⚡', desc:'EICR certificates, rewiring, consumer unit upgrades' },
  { value:'epc_assessor',   label:'EPC Assessor',    icon:'📊', desc:'Energy Performance Certificates for lettings' },
  { value:'plumber',        label:'Plumber',         icon:'🚿', desc:'Plumbing repairs, installations, Legionella checks' },
  { value:'general_builder',label:'General Builder', icon:'🏗', desc:'Maintenance, repairs, refurbishments' },
  { value:'locksmith',      label:'Locksmith',       icon:'🔑', desc:'Lock changes, security upgrades' },
  { value:'other',          label:'Other',           icon:'🛠', desc:'Other trade services' },
];

const ACCRED_ICONS = {
  'Gas Safe':'🔥', 'NICEIC':'⚡', 'RICS':'📊', 'NAPIT':'⚡',
};

const STATUS_LABELS = {
  pending:'Pending', viewed:'Viewed', quoted:'Quoted',
  accepted:'Accepted', declined:'Declined', completed:'Completed',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

const EMPTY_QUOTE = { property_id:'', trade:'', description:'', preferred_dates:'', landlord_notes:'' };

export default function Contractors() {
  const [contractors, setContractors] = useState([]);
  const [quotes, setQuotes]           = useState([]);
  const [properties, setProperties]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterTrade, setFilterTrade] = useState('');
  const [selected, setSelected]       = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm]     = useState(EMPTY_QUOTE);
  const [saving, setSaving]           = useState(false);
  const [tab, setTab]                 = useState('directory'); // directory | my-quotes
  const [saved, setSaved]             = useState('');

  const h = () => { const ac = localStorage.getItem('lc_access_code'); return { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('token')}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  async function load() {
    const [c, q, p] = await Promise.all([
      fetch(`/api/contractors${filterTrade ? `?trade=${filterTrade}` : ''}`, { headers: h() }).then(r => r.json()),
      fetch('/api/quote-requests', { headers: h() }).then(r => r.json()),
      fetch('/api/properties',     { headers: h() }).then(r => r.json()),
    ]);
    setContractors(Array.isArray(c) ? c : []);
    setQuotes(Array.isArray(q) ? q : []);
    setProperties(Array.isArray(p) ? p : []);
    setLoading(false);
  }

  useEffect(() => { load(); }, [filterTrade]);

  function openQuote(contractor) {
    setSelected(contractor);
    setQuoteForm({ ...EMPTY_QUOTE, trade: contractor.trades[0]?.trade || '' });
    setShowQuoteForm(true);
  }

  async function handleSendQuote() {
    if (!selected) return;
    setSaving(true);
    try {
      await fetch('/api/quote-requests', {
        method:'POST', headers: h(),
        body: JSON.stringify({
          contractor_id: selected.id,
          property_id: quoteForm.property_id || null,
          trade: quoteForm.trade,
          description: quoteForm.description,
          preferred_dates: quoteForm.preferred_dates || null,
          landlord_notes: quoteForm.landlord_notes || null,
        }),
      });
      const q = await fetch('/api/quote-requests', { headers: h() }).then(r => r.json());
      setQuotes(Array.isArray(q) ? q : []);
      setShowQuoteForm(false);
      setSaved(`Quote request sent to ${selected.company_name || selected.full_name}!`);
      setTimeout(() => setSaved(''), 5000);
      setTab('my-quotes');
    } finally { setSaving(false); }
  }

  async function cancelQuote(id) {
    await fetch(`/api/quote-requests/${id}`, {
      method:'PUT', headers: h(),
      body: JSON.stringify({ status:'declined' }),
    });
    setQuotes(qs => qs.map(q => q.id === id ? { ...q, status:'declined' } : q));
  }

  const set = k => e => setQuoteForm(f => ({ ...f, [k]: e.target.value }));

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Contractor <em>Marketplace</em></h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Find vetted local contractors for certificates, repairs and maintenance</p>
        </div>
        {quotes.filter(q => q.status === 'pending' || q.status === 'quoted').length > 0 && (
          <button onClick={() => setTab('my-quotes')} style={{ background:'var(--warn)', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {quotes.filter(q => q.status === 'pending' || q.status === 'quoted').length} active quotes
          </button>
        )}
      </div>

      {saved && (
        <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, padding:'12px 16px', marginBottom:20, color:'#15803d', fontWeight:600 }}>
          ✅ {saved}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        {['directory','my-quotes'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding:'8px 20px', borderRadius:20, border:'1.5px solid var(--border)',
            background: tab === t ? 'var(--ink)' : 'transparent',
            color: tab === t ? '#fff' : 'var(--ink)', fontSize:14, cursor:'pointer', fontWeight:500,
          }}>
            {t === 'directory' ? '🔧 Find a contractor' : `📋 My quotes (${quotes.length})`}
          </button>
        ))}
      </div>

      {tab === 'directory' && (
        <>
          {/* Trade filter */}
          <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
            {TRADES.map(t => (
              <button key={t.value} onClick={() => setFilterTrade(t.value)} style={{
                padding:'8px 16px', borderRadius:20, border:'1.5px solid var(--border)',
                background: filterTrade === t.value ? 'var(--green, #1B4332)' : 'transparent',
                color: filterTrade === t.value ? '#fff' : 'var(--ink)',
                fontSize:13, cursor:'pointer', fontWeight:500, whiteSpace:'nowrap',
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Contractor cards */}
          {contractors.length === 0 ? (
            <Card style={{ textAlign:'center', padding:48 }}>
              <p style={{ fontSize:32, marginBottom:12 }}>🔧</p>
              <p style={{ fontWeight:600, marginBottom:8 }}>No contractors found</p>
              <p style={{ color:'var(--ink-3)', fontSize:14 }}>
                {filterTrade ? `No ${TRADES.find(t => t.value === filterTrade)?.label || filterTrade} contractors available yet.` : 'No contractors listed yet.'}
              </p>
            </Card>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:20 }}>
              {contractors.map(c => (
                <Card key={c.id} style={{ display:'flex', flexDirection:'column' }}>
                  <div style={{ flex:1 }}>
                    {/* Header */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <p style={{ fontWeight:700, fontSize:16 }}>{c.company_name || c.full_name}</p>
                          {c.is_verified && (
                            <span style={{ fontSize:11, background:'#dcfce7', color:'#15803d', padding:'2px 8px', borderRadius:12, fontWeight:600 }}>
                              ✓ Verified
                            </span>
                          )}
                        </div>
                        {c.company_name && <p style={{ fontSize:13, color:'var(--ink-3)' }}>{c.full_name}</p>}
                        {c.postcode && <p style={{ fontSize:12, color:'var(--ink-3)' }}>📍 {c.postcode} · covers {c.coverage_radius_miles} miles</p>}
                      </div>
                    </div>

                    {/* Trades */}
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                      {c.trades.map(t => (
                        <div key={t.id} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6, padding:'4px 10px' }}>
                          <p style={{ fontSize:12, fontWeight:600 }}>{t.trade_label}</p>
                          {t.accreditation && (
                            <p style={{ fontSize:11, color:'var(--ink-3)' }}>
                              {ACCRED_ICONS[t.accreditation] || '🏅'} {t.accreditation} {t.accred_number ? `#${t.accred_number}` : ''}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Bio */}
                    {c.bio && <p style={{ fontSize:13, color:'var(--ink-2)', lineHeight:1.5, marginBottom:12 }}>{c.bio}</p>}

                    {/* Contact */}
                    <div style={{ display:'flex', gap:12, marginBottom:16 }}>
                      {c.phone && <a href={`tel:${c.phone}`} style={{ fontSize:13, color:'var(--accent)' }}>📞 {c.phone}</a>}
                      {c.website && <a href={c.website} target="_blank" rel="noreferrer" style={{ fontSize:13, color:'var(--accent)' }}>🌐 Website</a>}
                    </div>
                  </div>

                  <Button onClick={() => openQuote(c)} style={{ width:'100%', textAlign:'center' }}>
                    Request a quote →
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'my-quotes' && (
        <>
          {quotes.length === 0 ? (
            <Card style={{ textAlign:'center', padding:48 }}>
              <p style={{ fontSize:32, marginBottom:12 }}>📋</p>
              <p style={{ fontWeight:600, marginBottom:8 }}>No quote requests yet</p>
              <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>
                Browse the contractor directory and request quotes for work you need done.
              </p>
              <Button onClick={() => setTab('directory')}>Find a contractor →</Button>
            </Card>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {quotes.map(q => {
                const statusColor = {
                  pending:'#d97706', viewed:'#3b82f6', quoted:'#8b5cf6',
                  accepted:'#16a34a', declined:'#dc2626', completed:'#16a34a',
                }[q.status] || '#6b7280';

                return (
                  <Card key={q.id} style={{ padding:'16px 20px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                          <p style={{ fontWeight:600, fontSize:15 }}>{q.contractor_name}</p>
                          <span style={{ fontSize:12, fontWeight:600, color:statusColor, background:`${statusColor}20`, padding:'2px 10px', borderRadius:12 }}>
                            {STATUS_LABELS[q.status] || q.status}
                          </span>
                          <span style={{ fontSize:12, background:'var(--bg)', border:'1px solid var(--border)', padding:'2px 8px', borderRadius:8 }}>
                            {q.trade_label}
                          </span>
                        </div>
                        <p style={{ fontSize:14, color:'var(--ink-2)', marginBottom:6 }}>{q.description}</p>
                        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                          {q.property_address && <span style={{ fontSize:12, color:'var(--ink-3)' }}>🏘 {q.property_address}</span>}
                          {q.preferred_dates && <span style={{ fontSize:12, color:'var(--ink-3)' }}>📅 {q.preferred_dates}</span>}
                          {q.contractor_phone && <a href={`tel:${q.contractor_phone}`} style={{ fontSize:12, color:'var(--accent)' }}>📞 {q.contractor_phone}</a>}
                          <span style={{ fontSize:12, color:'var(--ink-3)' }}>Sent {formatDate(q.created_at)}</span>
                        </div>
                        {q.contractor_notes && (
                          <div style={{ marginTop:10, padding:'10px 12px', background:'#f0f9ff', borderRadius:8, border:'1px solid #bae6fd' }}>
                            <p style={{ fontSize:12, fontWeight:600, color:'#0369a1', marginBottom:4 }}>Contractor response:</p>
                            <p style={{ fontSize:13 }}>{q.contractor_notes}</p>
                            {q.quoted_amount_gbp && <p style={{ fontSize:15, fontWeight:700, marginTop:6 }}>Quote: £{(q.quoted_amount_gbp/100).toFixed(2)}</p>}
                          </div>
                        )}
                      </div>
                      {q.status === 'pending' && (
                        <Button size="sm" variant="ghost" onClick={() => cancelQuote(q.id)} style={{ color:'var(--danger)' }}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Quote request modal */}
      {showQuoteForm && selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowQuoteForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:520, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:4 }}>Request a quote</h3>
            <p style={{ fontSize:14, color:'var(--ink-3)', marginBottom:20 }}>
              from {selected.company_name || selected.full_name}
            </p>

            <FormField label="Service needed *">
              <select value={quoteForm.trade} onChange={set('trade')}>
                <option value="">Select service…</option>
                {selected.trades.map(t => (
                  <option key={t.id} value={t.trade}>{t.trade_label}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Property (optional)">
              <select value={quoteForm.property_id} onChange={set('property_id')}>
                <option value="">Select property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
              </select>
            </FormField>

            <FormField label="Describe the work needed *">
              <textarea value={quoteForm.description} onChange={set('description')} rows={4}
                placeholder="e.g. Annual gas safety inspection and certificate for a 2-bed flat. Combi boiler, no gas fire. Tenant is available weekday mornings." />
            </FormField>

            <FormField label="Preferred dates / availability">
              <input value={quoteForm.preferred_dates} onChange={set('preferred_dates')}
                placeholder="e.g. Any weekday morning, 2 weeks' notice needed" />
            </FormField>

            <FormField label="Additional notes">
              <textarea value={quoteForm.landlord_notes} onChange={set('landlord_notes')} rows={2}
                placeholder="Access instructions, parking, anything else…" />
            </FormField>

            <p style={{ fontSize:12, color:'var(--ink-3)', marginBottom:16 }}>
              The contractor will be notified by email and can respond via the platform. Your contact details will be shared once you accept their quote.
            </p>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
              <Button variant="ghost" onClick={() => setShowQuoteForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSendQuote} disabled={!quoteForm.trade || !quoteForm.description}>
                Send quote request →
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}